import { createFileRoute } from '@tanstack/react-router';
import { TargetsSettingsList, TargetsSettingsListItem } from '../../components/TargetsSettings';
import {
  Accordion,
  Box,
  ScrollAreaRoot,
  ScrollAreaScrollbar,
  ScrollAreaViewport,
  Text,
  styled,
} from '@streamelements/frontend-ui';
import { useQuery } from '@tanstack/react-query';
import { streamDestinationsQueryOptions } from '../../api/streamDestinations';
import { channelsOwnerAccessQueryOptions } from '../../api/channels';
import { ALLOWED_OUTPUT_COUNT_PER_PROVIDER, Destination } from '../../services/DestinationsApiClient';
import { z } from 'zod';
import { Fragment } from 'react/jsx-runtime';
import { useAllObsStreamingOutputs, useOutputsList } from '../../utils/useOutputsList';
import { useEffect } from 'react';
import { AllowedProvider, platformPrettyName } from '../../constants/common';
import { AddOutputForm } from '../../components/AddOutputForm';
import { PlusIcon } from '../../components/Icons';

const searchParamsSchema = z
  .object({
    itemId: z.string(),
    canvasId: z.string().optional(),
  })
  .partial()
  .optional()
  .default({})
  .catch({});

export const Route = createFileRoute('/_authenticated/settings/_layout/outputs-settings/')({
  validateSearch: searchParamsSchema.parse,
  component: TargetSettings,
  loader: async ({ context: { queryClient, serviceEnvironment, authContextProvider } }) => {
    return Promise.all([
      queryClient.ensureQueryData(
        channelsOwnerAccessQueryOptions({
          serviceEnvironment,
          authContextProvider,
        })
      ),
      queryClient.ensureQueryData(
        streamDestinationsQueryOptions({
          serviceEnvironment,
          authContextProvider,
        })
      ),
    ]);
  },
});

const Root = styled(TargetsSettingsList, {
  display: 'grid',
  gap: '0.5rem',
});

function TargetSettings() {
  const { itemId } = Route.useSearch();
  const { serviceEnvironment, authContextProvider } = Route.useRouteContext();

  const obsStreamingOutputs = useAllObsStreamingOutputs();

  const outputsList = useOutputsList({
    serviceEnvironment,
    authContextProvider,
  });

  const { data: destinations, refetch: refetchDestinations } = useQuery(
    streamDestinationsQueryOptions({
      serviceEnvironment,
      authContextProvider,
    })
  );

  useEffect(() => {
    function onOutputListChange() {
      refetchDestinations();
    }

    window.addEventListener('hostStreamingOutputListChanged', onOutputListChange);

    return () => {
      window.removeEventListener('hostStreamingOutputListChanged', onOutputListChange);
    };
  }, []);

  return (
    <ScrollAreaRoot type="always">
      <ScrollAreaViewport
        css={{
          pt: '0.25rem',
          px: '1.5rem',
          pb: '1.5rem',
          '> div': { minHeight: '100%' },
        }}
      >
        <Root type="multiple" defaultValue={itemId ? [itemId] : []}>
          {outputsList.map((channel) => {
            const destination =
              destinations.get(channel.id) ||
              ({
                id: channel.id,
                displayName:
                  channel.username in platformPrettyName
                    ? platformPrettyName[channel.username as AllowedProvider]
                    : channel.displayName,
                provider: channel.provider,
                outputs: Array(ALLOWED_OUTPUT_COUNT_PER_PROVIDER[channel.provider])
                  .fill(null)
                  .map((_, index) => ({
                    id: `${channel.id}-${channel.provider}-${index + 1}`,
                    name: channel.username,
                    isEnabled: true,
                    streamingSettings: {
                      serverUrl: '',
                      streamKey: '',
                    },
                    auxiliaryData: {
                      // destinationId: channel.id,
                    },
                  })),
              } as Destination);

            return (
              <Fragment key={destination.id}>
                {destination.outputs.map((output) => {
                  return (
                    <TargetsSettingsListItem.Content
                      key={output.id}
                      authContextProvider={authContextProvider}
                      serviceEnvironment={serviceEnvironment}
                      destination={destination}
                      output={output}
                      disabled={obsStreamingOutputs?.[output.id]?.isActive || output.auxiliaryData?.isPrimaryOutput}
                    >
                      {['custom'].includes(destination.provider) ? null : (
                        <TargetsSettingsListItem.Action
                          authContextProvider={authContextProvider}
                          serviceEnvironment={serviceEnvironment}
                          destinationMetadata={destination}
                          outputId={output.id}
                          disabled={obsStreamingOutputs?.[output.id]?.isActive || output.auxiliaryData?.isPrimaryOutput}
                        />
                      )}
                    </TargetsSettingsListItem.Content>
                  );
                })}
              </Fragment>
            );
          })}

          <TargetsSettingsListItem.Root value={'custom'}>
            <Accordion.Header>
              <TargetsSettingsListItem.Header css={{ gap: '0.75rem' }}>
                <PlusIcon size="lg" />
                <Box css={{ flex: 1 }}>
                  <Text.Subtitle weight="bold" css={{ margin: 0 }}>
                    Add output
                  </Text.Subtitle>
                </Box>
              </TargetsSettingsListItem.Header>
              <TargetsSettingsListItem.Trigger />
            </Accordion.Header>
            <Accordion.Content>
              <AddOutputForm
                authContextProvider={authContextProvider}
                serviceEnvironment={serviceEnvironment}
                defaultValue={{ provider: 'custom' }}
              />
            </Accordion.Content>
          </TargetsSettingsListItem.Root>
        </Root>
      </ScrollAreaViewport>
      <ScrollAreaScrollbar />
      <ScrollAreaScrollbar orientation="horizontal" />
    </ScrollAreaRoot>
  );
}
